// Payments Frontend App - Main entry point
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/hooks/useAuth";

// Lazy load page components for code splitting
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const Dashboard = lazy(() => import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const CustomersList = lazy(() => import("@/pages/CustomersList").then((m) => ({ default: m.CustomersList })));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail").then((m) => ({ default: m.CustomerDetail })));
const CustomerCreate = lazy(() => import("@/pages/CustomerCreate").then((m) => ({ default: m.CustomerCreate })));
const SubscriptionsList = lazy(() =>
  import("@/pages/SubscriptionsList").then((m) => ({ default: m.SubscriptionsList })),
);
const SubscriptionDetail = lazy(() =>
  import("@/pages/SubscriptionDetail").then((m) => ({ default: m.SubscriptionDetail })),
);
const PaymentsList = lazy(() => import("@/pages/PaymentsList").then((m) => ({ default: m.PaymentsList })));
const PaymentDetail = lazy(() => import("@/pages/PaymentDetail").then((m) => ({ default: m.PaymentDetail })));
const RefundsList = lazy(() => import("@/pages/RefundsList").then((m) => ({ default: m.RefundsList })));
const RefundDetail = lazy(() => import("@/pages/RefundDetail").then((m) => ({ default: m.RefundDetail })));
const Checkout = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.Checkout })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
const NotFound = lazy(() => import("@/pages/NotFound").then((m) => ({ default: m.NotFound })));
const Unauthorized = lazy(() => import("@/pages/Unauthorized").then((m) => ({ default: m.Unauthorized })));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NuqsAdapter>
          <AuthProvider>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/not-found" element={<NotFound />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <CustomersList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/new"
                    element={
                      <ProtectedRoute requiredScope="payments:write">
                        <CustomerCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/:id"
                    element={
                      <ProtectedRoute>
                        <CustomerDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscriptions"
                    element={
                      <ProtectedRoute>
                        <SubscriptionsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscriptions/:id"
                    element={
                      <ProtectedRoute>
                        <SubscriptionDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payments"
                    element={
                      <ProtectedRoute>
                        <PaymentsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payments/:id"
                    element={
                      <ProtectedRoute>
                        <PaymentDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/refunds"
                    element={
                      <ProtectedRoute>
                        <RefundsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/refunds/:id"
                    element={
                      <ProtectedRoute>
                        <RefundDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute requiredScope="payments:admin">
                        <Checkout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch-all */}
                  <Route path="*" element={<Navigate to="/not-found" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
